import React from "react";
import { Box, Button, Typography } from "@mui/material";
import HorizontalFilterBar from "../Search/FilterBar/HorizontalFilterBar";
import MobileSearchFilterBar from "../Search/mobile/MobileSearchFilterBar";
import { useFilterControllerContext } from "../Search/FilterBar/FilterControllerContext";
import { RESULT_VIEW_TABLE } from "../Search/FilterBar/ResultViewToggle";
import useIsMobile from "../../hooks/useIsMobile";
import {
  getBannerFlatBackground,
  getFilterBandBackground,
} from "../../components/layouts/SubHeader/subHeaderBannerStyles";
import CollapsedFiltersButton from "./CollapsedFiltersButton";
import SavedSearchBanner from "./SavedSearchBanner";

const SAVE_AS_NEW_SEARCH_LABEL = "Save as new search";

const FILTER_BAND_GRADIENT_V4_SX = {
  width: "100%",
  px: { xs: 2, md: "40px" },
  pt: { xs: 1.5, md: "16px" },
  pb: { xs: 1.5, md: "16px" },
  background: (theme) => getFilterBandBackground(theme),
};

/** V3: keep the banner lavender solid through the filter band so it can continue behind Enrich. */
const FILTER_BAND_LAVENDER_CONTINUE_SX = {
  width: "100%",
  px: { xs: 2, md: "40px" },
  pt: { xs: 1.5, md: "16px" },
  pb: { xs: 1.5, md: "16px" },
  background: (theme) => getBannerFlatBackground(theme),
};

export default function SavedSearchDetailChrome({
  workspace,
  bannerCount,
  isDirty,
  savedSearchOption,
  onDelete,
  onSaveSearch,
  saveSearchDisabled,
  onExport,
  exportDisabled = false,
  exportTitle,
  filterBarProps,
  continueLavenderBand = false,
  collapseFilters = false,
  filtersExpanded = true,
  onToggleFilters,
  resultsSource = "snapshot",
  sourceActions = {},
}) {
  const controller = useFilterControllerContext();
  const isMobile = useIsMobile();
  const isEditing = Boolean(controller?.isEditingSavedSearch);
  const showFilterToolbar = !collapseFilters || filtersExpanded || isEditing;
  const showForkWarning = !isEditing && !saveSearchDisabled;
  const showingWorkspaceSnapshot = resultsSource === "snapshot";
  const {
    runSavedQuery,
    runSavedQueryDisabled = false,
    returnToStoredSnapshot,
  } = sourceActions;
  const sharedFilterBarProps = {
    ...filterBarProps,
    resultView: RESULT_VIEW_TABLE,
    onResultViewChange: () => {},
    totalRows: bannerCount,
    onExport,
    exportDisabled,
    exportTitle,
    onSaveSearch,
    saveSearchDisabled,
    saveSearchLabel: SAVE_AS_NEW_SEARCH_LABEL,
  };

  return (
    <>
      <SavedSearchBanner
        name={workspace?.name || "Saved Search"}
        companyCount={bannerCount}
        isDirty={isDirty && !isEditing}
        isEditing={isEditing}
        showForkWarning={showForkWarning}
        savedSearch={savedSearchOption}
        onEditSearch={(option) => {
          controller?.initiateEditSearch?.(null, option || savedSearchOption);
        }}
        onDelete={onDelete}
        titleAccessory={
          collapseFilters && !isEditing ? (
            <CollapsedFiltersButton
              expanded={filtersExpanded}
              onToggle={onToggleFilters}
            />
          ) : null
        }
        subtitleAccessory={workspace ? (
          <Box
            data-testid="saved-search-source-controls"
            sx={{
              alignItems: "center",
              display: "inline-flex",
              flexWrap: "wrap",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              color="text.secondary"
              component="span"
              sx={{ fontSize: 12, fontWeight: 700 }}
            >
              {showingWorkspaceSnapshot
                ? "Stored snapshot"
                : resultsSource === "live"
                  ? "Live results"
                  : "Earlier live results · filters changed"}
            </Typography>
            {showingWorkspaceSnapshot ? (
              <Button
                size="small"
                onClick={runSavedQuery}
                disabled={runSavedQueryDisabled}
                sx={{ minWidth: 0, px: 0.75, py: 0, lineHeight: 1.4 }}
              >
                Run saved query
              </Button>
            ) : (
              <Button
                aria-label="Return to stored snapshot"
                size="small"
                onClick={returnToStoredSnapshot}
                sx={{ minWidth: 0, px: 0.75, py: 0, lineHeight: 1.4 }}
              >
                <span>Return to stored</span>&nbsp;<span>snapshot</span>
              </Button>
            )}
          </Box>
        ) : null}
      />

      {showFilterToolbar ? (
        <Box
          id="saved-search-filters-toolbar"
          sx={continueLavenderBand
            ? FILTER_BAND_LAVENDER_CONTINUE_SX
            : FILTER_BAND_GRADIENT_V4_SX}
        >
          {isMobile ? (
            <MobileSearchFilterBar {...sharedFilterBarProps} />
          ) : (
            <HorizontalFilterBar {...sharedFilterBarProps} />
          )}
        </Box>
      ) : continueLavenderBand ? (
        <Box
          aria-hidden="true"
          sx={{
            width: "100%",
            height: 12,
            background: (theme) => getBannerFlatBackground(theme),
          }}
        />
      ) : null}
    </>
  );
}
